<div class="v2-wrapper">

  <!-- Page Header -->
  <div class="page-header">
    <div class="header-left">
      <mat-icon class="header-icon">auto_awesome</mat-icon>
      <div>
        <h2 class="page-title">Analytics Dashboard</h2>
        <span class="page-subtitle">Ask a question about your data to generate charts and insights automatically</span>
      </div>
    </div>
  </div>

  <!-- NL Ask Section -->
  <div class="nl-ask-card">
    <div class="search-pill">
      <mat-icon class="pill-left-icon">auto_awesome</mat-icon>
      <input class="pill-input" [(ngModel)]="userQuestion"
        placeholder="Ask a question about your data..."
        (keyup.enter)="analyzeQuestion()">
      <button class="pill-send-btn"
        [disabled]="analyzing || !userQuestion.trim()"
        (click)="analyzeQuestion()"
        [title]="analyzing ? 'Analyzing...' : 'Analyze'">
        <mat-icon>{{ analyzing ? 'hourglass_empty' : 'arrow_upward' }}</mat-icon>
      </button>
    </div>
    <div *ngIf="nlError" class="nl-error">
      <mat-icon>error_outline</mat-icon> {{ nlError }}
    </div>
  </div>

  <!-- Forecast Info Card -->
  <mat-card *ngIf="forecastResponse" class="forecast-info-card">
    <mat-card-content>
      <div class="forecast-info-row">

        <!-- Query summary -->
        <div class="forecast-meta">
          <span class="forecast-query">{{ forecastResponse.query_summary?.original_query }}</span>
          <span class="forecast-metric">{{ forecastResponse.query_summary?.metric }}</span>
        </div>

        <!-- Trend badge -->
        <div class="trend-badge"
          *ngIf="forecastResponse.forecast?.trend_direction"
          [style.color]="trendColor(forecastResponse.forecast.trend_direction)">
          <span class="trend-icon">{{ trendIcon(forecastResponse.forecast.trend_direction) }}</span>
          <span class="trend-label">{{ forecastResponse.forecast.trend_direction }}</span>
          <span class="trend-confidence"
            *ngIf="forecastResponse.forecast.confidence != null">
            ({{ (forecastResponse.forecast.confidence * 100).toFixed(0) }}% confidence)
          </span>
        </div>

        <!-- Forecast window -->
        <div class="forecast-window" *ngIf="forecastResponse.forecast?.forecast_window">
          <mat-icon>schedule</mat-icon>
          {{ forecastResponse.forecast.forecast_window }}
        </div>

        <!-- Toggle buttons -->
        <div class="toggle-group">
          <button mat-stroked-button
            [class.toggle-active]="activeView === 'historical'"
            (click)="toggleView('historical')">
            <mat-icon>history</mat-icon> Historical
          </button>
          <button mat-stroked-button
            [class.toggle-active]="activeView === 'both'"
            (click)="toggleView('both')">
            <mat-icon>compare_arrows</mat-icon> Both
          </button>
          <button mat-stroked-button
            [class.toggle-active]="activeView === 'forecast'"
            (click)="toggleView('forecast')">
            <mat-icon>trending_up</mat-icon> Forecast
          </button>
        </div>

      </div>
    </mat-card-content>
  </mat-card>

  <!-- Skeleton Loading -->
  <div *ngIf="analyzing" class="skeleton-section">
    <div class="row g-3 mb-3">
      <div class="col-md-3" *ngFor="let _ of [1,2,3,4]">
        <div class="skeleton-kpi">
          <div class="skeleton-box skeleton-val"></div>
          <div class="skeleton-box skeleton-lbl"></div>
        </div>
      </div>
    </div>
    <div class="row g-3">
      <div class="col-md-8">
        <div class="skeleton-chart-card">
          <div class="skeleton-box skeleton-chart-title"></div>
          <div class="skeleton-box skeleton-chart-body"></div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="skeleton-chart-card">
          <div class="skeleton-box skeleton-chart-title"></div>
          <div class="skeleton-box skeleton-chart-body"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Dashboard Widgets -->
  <div class="dashboard-section" *ngIf="!analyzing">
    <ng-container *ngIf="widgets.length > 0; else emptyState">
      <div class="row g-3">
        <ng-container *ngFor="let widget of widgets">
          <div [class]="'col-md-' + widget.col" *ngIf="isChartVisible(widget)">

            <!-- KPI Group -->
            <ng-container *ngIf="widget.type === 'kpi-group'">
              <div class="row g-3">
                <div
                  *ngFor="let kpi of widget.kpis"
                  [class]="'col-md-' + (12 / (widget.kpis!.length <= 4 ? widget.kpis!.length : 4))">
                  <mat-card class="kpi-card" [style.border-top]="'4px solid ' + kpi.color">
                    <mat-card-content>
                      <div class="kpi-body">
                        <div>
                          <div class="kpi-value" [style.color]="kpi.color">{{ kpi.value }}</div>
                          <div class="kpi-label">{{ kpi.label }}</div>
                        </div>
                        <div class="kpi-dot" [style.background]="kpi.color"></div>
                      </div>
                    </mat-card-content>
                  </mat-card>
                </div>
              </div>
            </ng-container>

            <!-- Chart -->
            <mat-card *ngIf="widget.type === 'chart'" class="widget-card">
              <div class="widget-header">
                <span class="widget-title">{{ widget.title }}</span>
                <button mat-icon-button class="export-icon-btn"
                  matTooltip="Download PNG" (click)="exportChartPng(widget)">
                  <mat-icon>download</mat-icon>
                </button>
              </div>
              <mat-card-content>
                <highcharts-chart
                  [Highcharts]="Highcharts"
                  [options]="widget.chartOptions!"
                  (chartInstance)="onChartInstance($event, widget.id!)"
                  style="width:100%; height:300px; display:block;">
                </highcharts-chart>
              </mat-card-content>
            </mat-card>

            <!-- Table -->
            <mat-card *ngIf="widget.type === 'table'" class="widget-card">
              <div class="widget-header">
                <span class="widget-title">{{ widget.title }}</span>
                <button mat-icon-button class="export-icon-btn"
                  matTooltip="Download CSV" (click)="exportCSV(widget)">
                  <mat-icon>download</mat-icon>
                </button>
              </div>
              <mat-card-content>
                <div class="table-responsive">
                  <table class="auto-table">
                    <thead>
                      <tr>
                        <th *ngFor="let col of widget.columns">{{ col }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let row of widget.rows">
                        <td *ngFor="let cell of row">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </mat-card-content>
            </mat-card>

            <!-- Info message -->
            <div *ngIf="widget.type === 'message'" class="info-message">
              <mat-icon>info</mat-icon>
              <span>{{ widget.message }}</span>
            </div>

          </div>
        </ng-container>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="empty-state">
        <mat-icon>bar_chart</mat-icon>
        <p>Ask a question above to generate your analytics dashboard.</p>
        <span class="empty-hint">Try: "Show total complaints by district this month" or "Predict complaints for next 3 months"</span>
      </div>
    </ng-template>
  </div>

</div>
